'use client'

import { useAtomValue } from 'jotai'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import type { HTMLProps } from 'react'
import {
  useActivateAccountError,
  useActivateAccountIsMutating,
} from '@/src/hooks/account/use-activate-account'
import {
  invalidWalletKeyringAtom,
  isActiveAtom,
  isCompleteAtom,
} from '@/src/stores/route-machine/atoms'
import {
  useNetworkSelection,
  useTokenSelection,
} from '@/src/stores/token-selection/actions'
import { cn } from '@/src/utils/styles'
import { useModalMode } from '../modal-mode-context'
import { SwapForm } from '../swap-form'
import { GeneratingHotkey } from '../swap-form/generating-hotkey'
import { TradeOverview } from '../trade-overview'

export function TradeModuleWrapper({
  tradeOverviewClassName,
  generatingHotkeyClassName,
  swapFormClassName,
  className,
  hideScrim,
}: {
  tradeOverviewClassName?: string
  generatingHotkeyClassName?: string
  swapFormClassName?: string
  hideScrim?: boolean
  className?: string
}) {
  const isTradeActive = useAtomValue(isActiveAtom)
  const isComplete = useAtomValue(isCompleteAtom)
  const invalidWalletKeyring = useAtomValue(invalidWalletKeyringAtom)
  const {
    isOpen: isTokenSelectionOpen,
    close: closeTokenSelection,
    isAnimatedOpen,
  } = useTokenSelection()
  const { isOpen: isNetworkSelectionOpen, close: closeNetworkSelection } =
    useNetworkSelection()
  const modalMode = useModalMode()

  const isSelectionFlowOpen =
    isTokenSelectionOpen || isNetworkSelectionOpen || isAnimatedOpen

  const shouldDisplayTradeOverview =
    isTradeActive || isComplete || !!invalidWalletKeyring
  const isActivatingAccount = useActivateAccountIsMutating()
  const { isError: isActivatingAccountFailed } = useActivateAccountError()

  return (
    <div className={cn('w-full mx-auto', className)}>
      {!hideScrim && !modalMode && (
        <AnimatePresence>
          {isSelectionFlowOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className='fixed inset-0 w-screen h-screen bg-scrim z-20'
              onClick={() => {
                closeTokenSelection()
                closeNetworkSelection()
              }}
              transition={{ duration: 0.3 }}
            />
          )}
        </AnimatePresence>
      )}
      <AnimatePresence mode='popLayout'>
        {shouldDisplayTradeOverview && (
          <AnimationWrapper>
            <TradeOverview className={tradeOverviewClassName} />
          </AnimationWrapper>
        )}
      </AnimatePresence>
      <AnimatePresence mode='popLayout'>
        {!shouldDisplayTradeOverview && (
          <AnimationWrapper>
            {isActivatingAccount || isActivatingAccountFailed ? (
              <GeneratingHotkey className={generatingHotkeyClassName} />
            ) : (
              <div className='w-full min-h-[448px]'>
                <SwapForm className={swapFormClassName} />
              </div>
            )}
          </AnimationWrapper>
        )}
      </AnimatePresence>
    </div>
  )
}

function AnimationWrapper(
  props: HTMLProps<HTMLDivElement> & HTMLMotionProps<'div'>,
) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='w-full'
      transition={{ duration: 0.3 }}
      {...props}
    />
  )
}
